import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import NoteIcon from '@mui/icons-material/Note';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PushPinIcon from '@mui/icons-material/PushPin';
import { formatDistanceToNow } from 'date-fns';
import { enhancedApi, mapApiErrorsToForm } from '../api';
import { runValidators } from '../utils/validators';
import { useFormValidation } from '../hooks/useFormValidation';
import { fieldHelperText } from '../utils/formHelpers';
import { noteFormSchema } from './NoteForm';
import ConfirmDeleteModal from './ui/ConfirmDeleteModal';

const NOTE_META_SPLIT = '\n\n--beck-note-meta--\n';
const MAX_NOTE_LENGTH = 5000;
const NOTE_CATEGORIES = ['Payroll', 'Bookkeeping', 'Tax', 'Miscellaneous'];

const AVATAR_BG = [
  'primary.main',
  'secondary.main',
  'info.main',
  'success.main',
  'warning.dark',
  'error.light',
];

function hashHue(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = str.charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h);
}

function pickAvatarBg(key) {
  return AVATAR_BG[hashHue(String(key)) % AVATAR_BG.length];
}

function initials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function parseNoteContent(raw) {
  if (!raw || typeof raw !== 'string') {
    return { body: '', tags: [], pinned: false };
  }
  const idx = raw.lastIndexOf(NOTE_META_SPLIT);
  if (idx === -1) {
    return { body: raw, tags: [], pinned: false };
  }
  const body = raw.slice(0, idx);
  let tags = [];
  let pinned = false;
  try {
    const meta = JSON.parse(raw.slice(idx + NOTE_META_SPLIT.length));
    if (Array.isArray(meta.tags)) {
      tags = meta.tags.map((t) => String(t).trim()).filter(Boolean);
    }
    pinned = Boolean(meta.pinned);
  } catch {
    /* ignore corrupt meta */
  }
  return { body, tags, pinned };
}

function buildNoteContent(body, tags, pinned) {
  const tagList = tags.map((t) => String(t).trim()).filter(Boolean);
  const meta = JSON.stringify({ tags: tagList, pinned: Boolean(pinned) });
  return `${String(body).trimEnd()}${NOTE_META_SPLIT}${meta}`;
}

function resolveCategoryFromTags(tags) {
  const match = tags.find((t) => NOTE_CATEGORIES.includes(t));
  return match || 'Miscellaneous';
}

function enrichNote(note) {
  const { body, tags: metaTags, pinned } = parseNoteContent(note.content);
  const category = note.category || 'Miscellaneous';
  const chipTags = new Set(metaTags);
  if (category && category !== 'Miscellaneous' && metaTags.length === 0) {
    chipTags.add(category);
  }
  return {
    ...note,
    _body: body,
    _chipTags: [...chipTags],
    _pinned: pinned,
  };
}

function sortNotesRows(rows) {
  return [...rows].sort((a, b) => {
    if (a._pinned !== b._pinned) return a._pinned ? -1 : 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });
}

function profileDisplayName(data) {
  const d = data?.data ?? data;
  if (!d) return null;
  const name = (d.profile_name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || d.username || '').trim();
  return name || null;
}

const NotesTab = ({ client }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffNames, setStaffNames] = useState({});
  const staffRequested = useRef(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [noteBody, setNoteBody] = useState('');
  const [tagTokens, setTagTokens] = useState([]);
  const [pinNote, setPinNote] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const {
    errors: noteFieldErrors,
    touched: noteFieldTouched,
    validateField: noteValidateField,
    touchField: noteTouchField,
    validateAll: noteValidateAll,
    clearErrors: noteClearFieldErrors,
    mergeServerFieldErrors: noteMergeServerErrors,
  } = useFormValidation(noteFormSchema);

  const noteFormValues = useMemo(() => ({ content: noteBody }), [noteBody]);

  const noteAllValid = useMemo(
    () => !Object.keys(noteFormSchema).some((k) => runValidators(noteFormValues[k], noteFormSchema[k])),
    [noteFormValues]
  );

  const fetchNotes = useCallback(async () => {
    if (!client?.id) {
      setNotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await enhancedApi.getClientNotes(client.id);
      const list = Array.isArray(response.data) ? response.data : [];
      setNotes(list.map(enrichNote));
    } catch {
      setNotes([]);
      setSnackbar({ open: true, message: 'Could not load notes.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [client?.id]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    staffRequested.current.clear();
    setStaffNames({});
  }, [client?.id]);

  const sortedRows = useMemo(() => sortNotesRows(notes), [notes]);

  useEffect(() => {
    const ids = [...new Set(notes.map((n) => n.user).filter(Boolean).map(String))];
    ids.forEach((userId) => {
      if (staffRequested.current.has(userId)) return;
      staffRequested.current.add(userId);
      enhancedApi
        .getUserProfile(userId)
        .then((res) => {
          const name = profileDisplayName(res.data) || `User ${userId}`;
          setStaffNames((prev) => ({ ...prev, [userId]: name }));
        })
        .catch(() => {
          setStaffNames((prev) => ({ ...prev, [userId]: `User ${userId}` }));
        });
    });
  }, [notes]);

  const openAddDialog = () => {
    setEditingId(null);
    setNoteBody('');
    setTagTokens([]);
    setPinNote(false);
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    noteClearFieldErrors();
    const { body, tags: metaTags, pinned } = parseNoteContent(row.content);
    const category = row.category || 'Miscellaneous';
    const tagsForEdit =
      metaTags.length > 0
        ? metaTags
        : category !== 'Miscellaneous'
          ? [category]
          : [];
    setEditingId(row.id);
    setNoteBody(body);
    setTagTokens(tagsForEdit);
    setPinNote(pinned);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setNoteBody('');
    setTagTokens([]);
    setPinNote(false);
  };

  const handleSave = async () => {
    const trimmed = noteBody.trim();
    if (!client?.id) return;
    if (!noteValidateAll({ content: trimmed })) return;

    const content = buildNoteContent(trimmed, tagTokens, pinNote);
    const category = resolveCategoryFromTags(tagTokens);

    setSaveLoading(true);
    try {
      if (editingId != null) {
        const res = await enhancedApi.createClientNote(client.id, {
          title: '',
          content,
          category,
        });
        const replacement = enrichNote(res.data);
        await enhancedApi.deleteClientNote(client.id, editingId);
        setNotes((prev) => sortNotesRows([...prev.filter((n) => n.id !== editingId), replacement]));
      } else {
        const res = await enhancedApi.createClientNote(client.id, {
          title: '',
          content,
          category,
        });
        setNotes((prev) => sortNotesRows([...prev, enrichNote(res.data)]));
      }
      noteClearFieldErrors();
      closeDialog();
    } catch (err) {
      const mapped = mapApiErrorsToForm(err?.response?.data);
      if (Object.keys(mapped).length) noteMergeServerErrors(mapped);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Could not save note.';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !client?.id) return;
    setDeleteLoading(true);
    try {
      await enhancedApi.deleteClientNote(client.id, deleteTarget.id);
      setNotes((prev) => prev.filter((n) => n.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      setSnackbar({ open: true, message: 'Could not delete note.', severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ width: 1, display: 'flex', flexDirection: 'column', gap: 3, pb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notes
        </Typography>
        <Button variant="contained" startIcon={<NoteAddIcon />} onClick={openAddDialog} disabled={!client?.id}>
          Add Note
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : sortedRows.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            px: 2,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <NoteIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No notes yet
          </Typography>
          <Button variant="outlined" startIcon={<NoteAddIcon />} onClick={openAddDialog} disabled={!client?.id}>
            Add first note
          </Button>
        </Box>
      ) : (
        <Stack spacing={2}>
          {sortedRows.map((row) => {
            const userId = row.user != null ? String(row.user) : '';
            const displayName = staffNames[userId] || (userId ? `User ${userId}` : 'Staff');
            const rel = row.created_at
              ? formatDistanceToNow(new Date(row.created_at), { addSuffix: true })
              : '';

            return (
              <Card
                key={row.id}
                elevation={0}
                sx={{
                  position: 'relative',
                  border: 1,
                  borderColor: 'divider',
                  borderLeftWidth: row._pinned ? 3 : 1,
                  borderLeftColor: row._pinned ? 'warning.main' : 'divider',
                  boxShadow: 1,
                  transition: (theme) =>
                    theme.transitions.create(['box-shadow', 'border-color'], {
                      duration: theme.transitions.duration.short,
                    }),
                  '&:hover': {
                    boxShadow: 3,
                    '& .note-actions': { opacity: 1 },
                  },
                }}
              >
                {row._pinned && (
                  <PushPinIcon
                    sx={{
                      position: 'absolute',
                      top: (t) => t.spacing(1),
                      right: (t) => t.spacing(1),
                      fontSize: '1.1rem',
                      color: 'warning.main',
                      pointerEvents: 'none',
                    }}
                  />
                )}
                <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 }, pr: row._pinned ? 5 : 2 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        fontSize: '0.875rem',
                        bgcolor: pickAvatarBg(userId || displayName),
                      }}
                    >
                      {initials(displayName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {displayName}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                            {rel}
                          </Typography>
                          <Box
                            className="note-actions"
                            sx={{
                              display: 'inline-flex',
                              opacity: { xs: 1, sm: 0 },
                              transition: (theme) => theme.transitions.create('opacity'),
                            }}
                          >
                            <IconButton
                              size="small"
                              aria-label="Edit note"
                              onClick={() => openEditDialog(row)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Delete note"
                              onClick={() =>
                                setDeleteTarget({
                                  id: row.id,
                                  preview: (row._body || '').slice(0, 120),
                                })
                              }
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Stack>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.25 }}>
                        {rel}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: 'text.primary',
                        }}
                      >
                        {row._body}
                      </Typography>
                      {row._chipTags.length > 0 && (
                        <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 2 }}>
                          {row._chipTags.map((t) => (
                            <Chip key={`${row.id}-${t}`} size="small" label={t} variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, pr: 6 }}>
          {editingId != null ? 'Edit note' : 'Add note'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={4}
              label="Note"
              placeholder="Write your note…"
              value={noteBody}
              onChange={(e) => {
                const v = e.target.value.slice(0, MAX_NOTE_LENGTH);
                setNoteBody(v);
                if (noteFieldTouched.content) noteValidateField('content', v);
              }}
              onBlur={() => noteTouchField('content', noteBody)}
              error={Boolean(noteFieldTouched.content && noteFieldErrors.content)}
              inputProps={{ maxLength: MAX_NOTE_LENGTH }}
              helperText={fieldHelperText(
                noteFieldTouched.content && noteFieldErrors.content,
                MAX_NOTE_LENGTH,
                noteBody.length
              )}
              FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={tagTokens}
              onChange={(_, v) => setTagTokens(v.map((t) => String(t).trim()).filter(Boolean))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={`${option}-${index}`} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Type and press Enter" />
              )}
            />
            <FormControlLabel
              control={<Switch checked={pinNote} onChange={(e) => setPinNote(e.target.checked)} color="warning" />}
              label="Pin note"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog} color="inherit" disabled={saveLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!noteAllValid || saveLoading || !client?.id}
            startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        onClose={() => !deleteLoading && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        itemName={(deleteTarget?.preview || 'Note').trim() || 'Note'}
        itemType="note"
        loading={deleteLoading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotesTab;
